import { create } from 'zustand';
import type { WeeklyAttendance } from '@/types/user';

export type UserRole =
  | 'BCT_ADMIN'
  | 'BCT_PM'
  | 'BCT_CONSULTANT'
  | 'CUSTOMER_ADMIN'
  | 'CUSTOMER_USER';

/**
 * User interface for authenticated user (from auth store).
 *
 * PII HANDLING:
 * - Contains email and name (PII) for the authenticated user only
 * - Stored in memory (Zustand store) - cleared on logout
 * - Never persisted to localStorage/sessionStorage
 * - Users can view their own PII (profile page)
 * - For admin views of other users, use admin/types.ts User interface
 *
 * RETENTION: Cleared immediately on logout via clearAuth()
 */
/** Mirrors the `NotificationPreference` enum in prisma/schema.prisma. */
export type NotificationPreference = 'ALL' | 'IN_APP_ONLY' | 'NONE';

/** How often notification emails arrive. In-app notifications are always immediate. */
export type EmailDigestMode = 'IMMEDIATE' | 'BATCHED' | 'DAILY';

/**
 * The themes the design system can render: `@bct/styleguide` exports exactly
 * these four as `ThemeColor`, and `index.css` defines exactly these four.
 */
export const BCT_THEMES = ['discovery', 'governance', 'operations', 'corsa'] as const;

export const DEFAULT_BCT_THEME: BctTheme = 'governance';

export type BctTheme = (typeof BCT_THEMES)[number];

export interface User {
  id: string;
  /** PII: Email address - authenticated user's own data */
  email: string;
  /** PII: Full name - authenticated user's own data */
  name: string;
  role: UserRole;
  tenantId: string | null;
  profilePictureUrl: string | null;
  authProvider: 'EMAIL' | 'MICROSOFT' | 'GOOGLE';
  isActive: boolean;
  notificationPreference?: NotificationPreference;
  emailDigestMode?: EmailDigestMode;
  preferredLanguage?: string;
  /** PII: Phone number - user-controlled visibility */
  phoneNumber?: string | null;
  phoneVisible?: boolean;
  /** Weekly work schedule - user-controlled visibility */
  weeklyAttendance?: WeeklyAttendance | null;
  attendanceVisible?: boolean;
  /** BCT product theme for this user's organization (set by platform admin) */
  organizationTheme?: BctTheme;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Tracks whether this is a fresh login or a page refresh/token restoration */
  loginContext: 'fresh' | 'refresh' | null;
  setUser: (user: User | null) => void;
  setAccessToken: (token: string | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setLoading: (isLoading: boolean) => void;
  setLoginContext: (context: 'fresh' | 'refresh' | null) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true,
  loginContext: null,

  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
    }),

  setAccessToken: (accessToken) =>
    set({
      accessToken,
      isAuthenticated: !!accessToken,
    }),

  setTokens: (accessToken, _refreshToken) =>
    set({
      accessToken,
      isAuthenticated: !!accessToken,
      // Note: refreshToken is handled via httpOnly cookie, not stored in memory
    }),

  setLoading: (isLoading) => set({ isLoading }),

  setLoginContext: (loginContext) => set({ loginContext }),

  clearAuth: () => {
    // PII RETENTION: Clear all user data (including PII) from memory on logout
    // This ensures no PII persists after session ends
    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      loginContext: null,
    });
  },
}));

export function isBctStaff(role: UserRole): boolean {
  return ['BCT_ADMIN', 'BCT_PM', 'BCT_CONSULTANT'].includes(role);
}

/**
 * Alias for isBctStaff - checks if user has organization staff role.
 * Use this name for multi-org environments where "BCT" terminology doesn't apply.
 */
export const isOrgStaff = isBctStaff;
